import React, {
	useEffect,
	useState,
	useContext,
	useCallback,
	useMemo,
} from "react";
import { useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Grid from "@mui/material/Grid";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";

import DeviceApi from "../../api/device";
import UpdateDeviceDto, {
	Device,
	DeviceError,
	DeviceStatus,
	FetchDeviceWithHistoryDto,
} from "../../models/Device";
import { AppContext } from "../../Contexts/AppContext";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Chip,
	ChipPropsColorOverrides,
	CircularProgress,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	OutlinedInput,
	Stack,
	Tooltip,
} from "@mui/material";
import { Permissionary } from "../../models/Permissionary";
import { Zone } from "../../models/Zone";
import PermissionaryApi from "../../api/permissionary";
import { DeleteForever } from "@mui/icons-material";
import DeleteDeviceModal from "../DevicesScreen/components/DeleteDeviceModal/DeleteDeviceModal";
import { useBus } from "react-bus";
import ExceptionsAccordion from "./components/ExceptionsAccordion/ExceptionsAccordion";
import { isAxiosError } from "axios";
import { UserRole } from "../../models/User";
import { DatesUtils } from "../../utils/DatesUtils";
import { isEmpty } from "lodash";
import { PermissionaryConfiguration } from "../../models/PermissionaryConfiguration";
import { getDerivedDeviceStatus } from "../../utils/deviceStatus";
import PermissionaryConfigurationApi from "../../api/permissionary-configuration";
import { CabinetState, DeviceHistory } from "../../models/DeviceHistory";

export default function DeviceDetailsScreen() {
	document.title = "Detalles del dispositivo";
	const navigate = useNavigate();
	const { deviceId } = useParams();
	const bus = useBus();
	const [device, setDevice] = useState<
		UpdateDeviceDto | FetchDeviceWithHistoryDto
	>({
		_id: "",
		id: "",
		deviceId: "",
		deviceType: "",
		address: "",
		connectionType: "",
		socketId: "",
		permissionaryId: "",
		city: "",
		state: "",
		ipAddress: "",
		port: 0,
		lastLoadedProfileDate: "",
		lastLoadedProfileDateUtc: "",
		customerId: "",
		isBackUp: false,
		timeZone: "",
		accountNumber: "",
		commAddress: 0,
		createdAt: "",
		updatedAt: "",
		processing: false,
		notes: "",
		exceptions: [],
		status: DeviceStatus.ONLINE,
	});
	const [zone, setZone] = useState<Zone | null>(null);
	const [permissionaryConfiguration, setPermissionaryConfiguration] =
		useState<PermissionaryConfiguration | null>(null);
	const [lastLoaded, setLastLoaded] = useState<Date>(new Date());
	const [loading, setLoading] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [editModeState, setEditModeState] = useState<boolean>(false);
	const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);

	const { setTitle, userContext, snackBar, setSnackBar } =
		useContext(AppContext);

	const fetchPermissionaryConfiguration = useCallback(async () => {
		if (!userContext?.permissionaryId) {
			setPermissionaryConfiguration(null);
			return;
		}
		try {
			const result: PermissionaryConfiguration =
				await PermissionaryConfigurationApi.fetchConfiguration(
					userContext.permissionaryId,
					userContext.accessToken,
				);
			setPermissionaryConfiguration(result);
		} catch (error) {
			setPermissionaryConfiguration(null);
		}
	}, [userContext]);

	const fetchDevice = useCallback(async () => {
		if (!userContext?.permissionaryId) return;

		setLoading(true);
		try {
			const token = localStorage.getItem("accessToken");
			const response = (await DeviceApi.getOneDevice({
				token,
				query: {
					permissionaryId: userContext.permissionaryId,
					_id: deviceId,
				},
				includeExceptions: true,
			})) as Device;

			setDevice(response);
		} catch (excepction: any) {
			console.error(excepction);
		} finally {
			setLoading(false);
		}
	}, [userContext, deviceId]);

	const handleRefresh = useCallback(async () => {
		await Promise.all([fetchDevice(), fetchPermissionaryConfiguration()]);
	}, [fetchDevice, fetchPermissionaryConfiguration]);

	const derivedExceptions = useMemo(() => {
		const currentDevice = device as FetchDeviceWithHistoryDto;
		const exceptions: DeviceError[] = [];

		if (isEmpty(currentDevice.history)) {
			return exceptions;
		}

		const newestDeviceHistory = currentDevice.history[0];
		const derivedStatus = getDerivedDeviceStatus(
			currentDevice,
			permissionaryConfiguration,
		);

		if (derivedStatus === DeviceStatus.OFFLINE) {
			exceptions.push(DeviceError.DEVICE_HISTORY_OFFLINE);
		}
		if (derivedStatus === DeviceStatus.ERROR) {
			exceptions.push(DeviceError.DEVICE_HISTORY_ERROR);
		}

		if (newestDeviceHistory.cabinetState === CabinetState.OPEN) {
			exceptions.push(DeviceError.DEVICE_HISTORY_CABINET_OPEN);
		}

		return exceptions;
	}, [device, permissionaryConfiguration]);

	const handleSaveDevice = useCallback(async () => {
		try {
			setIsSaving(true);
			const dto = {
				...(device as UpdateDeviceDto),
				port: Number((device as UpdateDeviceDto).port),
				commAddress: Number((device as UpdateDeviceDto).commAddress),
			};

			await DeviceApi.updateDevice({
				token: localStorage.getItem("accessToken"),
				dto,
			});

			setSnackBar({
				...snackBar,
				open: true,
				severity: "success",
				message: "Dispositivo actualizado",
			});
			setEditModeState(false);
			await handleRefresh();
		} catch (error) {
			setSnackBar({
				...snackBar,
				open: true,
				severity: "error",
				message: "Error actualizando dispositivo",
			});
		} finally {
			setIsSaving(false);
		}
	}, [device, handleRefresh, setSnackBar, snackBar]);

	const handleCancelEdit = useCallback(async () => {
		setEditModeState(false);
		await fetchDevice();
	}, [fetchDevice]);

	const setProp = useCallback(
		(prop: string, value: any) => {
			setDevice({
				...device,
				[prop]: value,
			});
		},
		[device],
	);

	const deviceAnalyze = useCallback(async () => {
		if (!device._id) return;
		try {
			await DeviceApi.triggerDeviceAnalysis({
				id: device._id,
				accessToken: localStorage.getItem("accessToken") || "",
			});
			setSnackBar({
				...snackBar,
				open: true,
				severity: "success",
				message: "Comenzamos a analizar el dispositivo, vuelva en unos minutos",
			});
		} catch (error) {
			let errMsg = "Error al analizar el dispositivo";
			if (isAxiosError(error)) {
				errMsg =
					error.response?.data?.message || "Error al analizar el dispositivo";
			}
			setSnackBar({
				...snackBar,
				open: true,
				severity: "error",
				message: errMsg,
			});
		} finally {
		}
	}, [device]);

	const getOverallStatus = (device: FetchDeviceWithHistoryDto) => {
		if (!permissionaryConfiguration) return;
		let label = "Fuera de linea";
		let color:
			| "success"
			| "info"
			| "warning"
			| "error"
			| "default"
			| "primary"
			| "secondary" = "default";

		const derivedStatus = getDerivedDeviceStatus(
			device,
			permissionaryConfiguration,
		);

		if (derivedStatus === DeviceStatus.OFFLINE) {
			label = "Fuera de linea";
			color = "default";
		} else if (derivedStatus === DeviceStatus.ONLINE) {
			label = "En linea";
			color = "success";
		} else if (derivedStatus === DeviceStatus.ERROR) {
			label = "Error";
			color = "error";
		}

		return <Chip size='small' label={label} color={color} variant='outlined' />;
	};
	const getConnectivityStatus = (device: FetchDeviceWithHistoryDto) => {
		const derivedStatus = getDerivedDeviceStatus(
			device,
			permissionaryConfiguration,
		);

		if (derivedStatus === DeviceStatus.OFFLINE) {
			return (
				<Box display={"flex"} gap={1}>
					<Tooltip title='Fuera de linea' arrow>
						<ErrorIcon color='disabled' />
					</Tooltip>
					<Typography sx={{ color: "grey" }}>Fuera de linea</Typography>
				</Box>
			);
		}

		if (derivedStatus === DeviceStatus.ONLINE) {
			return (
				<Box display={"flex"} gap={1}>
					<Tooltip title='En linea' arrow>
						<CheckCircleIcon color='success' />
					</Tooltip>
					<Typography>En linea</Typography>
				</Box>
			);
		}

		if (derivedStatus === DeviceStatus.ERROR) {
			return (
				<Box display={"flex"} gap={1}>
					<Tooltip title='Error de Conexion' arrow>
						<ErrorIcon color='error' />
					</Tooltip>
					<Typography>Error de conexion</Typography>
				</Box>
			);
		}

		return null;
	};

	useEffect(() => {
		setTitle("Detalles del Dispositivo");
	}, [setTitle]);

	useEffect(() => {
		fetchDevice();
	}, [fetchDevice, lastLoaded]);

	useEffect(() => {
		fetchPermissionaryConfiguration();
	}, [fetchPermissionaryConfiguration]);

	useEffect(() => {
		if (device) {
			setDeviceToDelete(device as Device);
		}
	}, [device]);

	return (
		<Box
			sx={{
				minHeight: "100%",
				width: "100%",
				px: { xs: 2, md: 4 },
				py: { xs: 3, md: 4 },
				background:
					"radial-gradient(1200px 600px at 10% -10%, rgba(14, 165, 233, 0.18), transparent 60%), radial-gradient(900px 600px at 100% 0%, rgba(34, 197, 94, 0.12), transparent 60%), linear-gradient(180deg, #f6f7fb 0%, #edf2f7 100%)",
			}}>
			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
					<CircularProgress size={110} thickness={4} />
				</Box>
			) : (
				<Box sx={{ maxWidth: 1000, mx: "auto" }}>
					<Card
						id='deviceDetailScreenCard'
						sx={{
							borderRadius: 4,
							border: "1px solid rgba(15, 23, 42, 0.08)",
							backgroundColor: "rgba(255, 255, 255, 0.92)",
							boxShadow: "0 30px 60px rgba(15, 23, 42, 0.12)",
							backdropFilter: "blur(10px)",
						}}>
						<CardContent sx={{ p: { xs: 3, md: 4 } }}>
							<Stack
								direction={{ xs: "column", md: "row" }}
								alignItems={{ xs: "flex-start", md: "center" }}
								justifyContent='space-between'
								spacing={2}
								sx={{ mb: 2 }}>
								<Stack direction='row' spacing={1.5} alignItems='center'>
									<IconButton
										color='primary'
										aria-label='volver a dispositivos'
										onClick={() => {
											navigate("/devices");
										}}>
										<ArrowBackIcon />
									</IconButton>
									<Box>
										<Typography
											variant='h5'
											sx={{
												fontWeight: 700,
												color: "#0f172a",
												letterSpacing: "-0.02em",
											}}>
											Detalles del dispositivo
										</Typography>
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.7)" }}>
											Consulta y actualiza la configuracion operacional.
										</Typography>
									</Box>
								</Stack>
								<Stack direction='row' spacing={1} alignItems='center'>
									<Button
										variant='outlined'
										size='small'
										startIcon={<RefreshIcon />}
										disabled={loading || isSaving}
										onClick={handleRefresh}>
										Actualizar
									</Button>
									{(userContext?.role === UserRole.ADMIN ||
										userContext?.role === UserRole.GOD) &&
										(editModeState ? (
											<>
												<Button
													variant='outlined'
													size='small'
													startIcon={<CancelIcon />}
													disabled={loading || isSaving}
													onClick={handleCancelEdit}>
													Cancelar
												</Button>
												<Button
													variant='contained'
													size='small'
													startIcon={<SaveIcon />}
													disabled={loading || isSaving}
													onClick={handleSaveDevice}>
													Guardar
												</Button>
											</>
										) : (
											<Button
												variant='contained'
												size='small'
												startIcon={<EditIcon />}
												disabled={loading || isSaving}
												onClick={() => setEditModeState(true)}>
												Editar
											</Button>
										))}
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Estado:
									</Typography>
									{getOverallStatus(device as FetchDeviceWithHistoryDto)}
								</Stack>
							</Stack>
							<Divider sx={{ mb: 3 }} />
							<Grid container spacing={2.5}>
								{/*Blocked identifier cannot be changed */}
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Device ID
									</Typography>
									<Typography
										variant='body2'
										sx={{ fontWeight: 600, color: "#0f172a" }}>
										{device?.deviceId || "-"}
									</Typography>
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Ultima fecha de carga (local)
									</Typography>
									<Typography
										variant='body2'
										sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
										{DatesUtils.convertUtcToLocal(
											device?.lastLoadedProfileDateUtc,
										)}
									</Typography>
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Ultima fecha de carga universal
									</Typography>
									<Typography
										variant='body2'
										sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
										{device?.lastLoadedProfileDateUtc}
									</Typography>
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Zona horaria
									</Typography>
									<Typography
										variant='body2'
										sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
										{device?.timeZone || "-"}
									</Typography>
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Status
									</Typography>
									{isEmpty((device as FetchDeviceWithHistoryDto).history) ? (
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.65)" }}>
											No historial disponible
										</Typography>
									) : (
										<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
											{getConnectivityStatus(
												device as FetchDeviceWithHistoryDto,
											)}
										</Box>
									)}
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Cliente
									</Typography>
									<Typography
										variant='body2'
										sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
										{device.customerId || "-"}
									</Typography>
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Estado de Gabinete
									</Typography>
									{isEmpty((device as FetchDeviceWithHistoryDto).history) ? (
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.65)" }}>
											No data
										</Typography>
									) : (
										<Box>
											{(device as FetchDeviceWithHistoryDto).history[0]
												.cabinetState === CabinetState.OPEN ? (
												<Box display={"flex"} gap={1}>
													<Tooltip title='Abierto' arrow>
														<ErrorIcon color='error' />
													</Tooltip>
													<Typography>Abierto</Typography>
												</Box>
											) : (
												<Box display={"flex"} gap={1}>
													<Tooltip title='Cerrado' arrow>
														<CheckCircleIcon color='success' />
													</Tooltip>
													<Typography>Cerrado</Typography>
												</Box>
											)}
										</Box>
									)}
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Voltaje Principal
									</Typography>
									<Typography
										variant='body2'
										sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
										{isEmpty((device as FetchDeviceWithHistoryDto).history)
											? "No data"
											: `${
													(device as FetchDeviceWithHistoryDto).history[0]
														.voltageMeasurement
												}V`}
									</Typography>
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Temperatura
									</Typography>
									<Typography
										variant='body2'
										sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
										{isEmpty((device as FetchDeviceWithHistoryDto).history)
											? "No data"
											: (
													device as FetchDeviceWithHistoryDto
												).history[0].temperatureMeasurement.toFixed(2) +
												" " +
												(device as FetchDeviceWithHistoryDto).history[0]
													.temperatureUnits}
									</Typography>
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Ultima fecha de reporte
									</Typography>
									<Typography
										variant='body2'
										sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
										{isEmpty((device as FetchDeviceWithHistoryDto).history)
											? "No data"
											: DatesUtils.convertTimestampToLocal(
													(device as FetchDeviceWithHistoryDto).history[0]
														.timestamp,
												)}
									</Typography>
								</Grid>

								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Estado
									</Typography>
									{!editModeState && (
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
											{device?.state === "None" || !device?.state
												? "No definido"
												: device?.state}
										</Typography>
									)}
									{editModeState && (
										<Box sx={{ mt: 1, width: "100%" }}>
											<FormControl sx={{ width: "100%" }} variant='outlined'>
												<InputLabel htmlFor='outlined-adornment-address'>
													Estado
												</InputLabel>
												<OutlinedInput
													id='outlined-adornment-address'
													type={"text"}
													placeholder='Estado'
													label='Estado'
													multiline
													value={device?.state}
													onChange={(event) => {
														setProp("state", event.target.value);
													}}
												/>
											</FormControl>
										</Box>
									)}
								</Grid>

								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Ciudad
									</Typography>
									{!editModeState && (
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
											{device?.city ? device?.city : "No definido"}
										</Typography>
									)}
									{editModeState && (
										<Box sx={{ mt: 1, width: "100%" }}>
											<FormControl sx={{ width: "100%" }} variant='outlined'>
												<InputLabel htmlFor='outlined-adornment-address'>
													Ciudad
												</InputLabel>
												<OutlinedInput
													id='outlined-adornment-address'
													type={"text"}
													placeholder='Ciudad'
													label='Ciudad'
													multiline
													value={device?.city}
													onChange={(event) => {
														setProp("city", event.target.value);
													}}
												/>
											</FormControl>
										</Box>
									)}
								</Grid>

								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Direccion
									</Typography>
									{!editModeState && (
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
											{device?.address}
										</Typography>
									)}
									{editModeState && (
										<Box sx={{ mt: 1, width: "100%" }}>
											<FormControl sx={{ width: "100%" }} variant='outlined'>
												<InputLabel htmlFor='outlined-adornment-address'>
													Direccion
												</InputLabel>
												<OutlinedInput
													id='outlined-adornment-address'
													type={"text"}
													placeholder='Direccion'
													label='Direccion'
													multiline
													value={device?.address}
													onChange={(event) => {
														setProp("address", event.target.value);
													}}
												/>
											</FormControl>
										</Box>
									)}
								</Grid>

								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Medidor IP
									</Typography>
									{!editModeState && (
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
											{device?.ipAddress}
										</Typography>
									)}
									{editModeState && (
										<Box sx={{ mt: 1, width: "100%" }}>
											<FormControl sx={{ width: "100%" }} variant='outlined'>
												<InputLabel htmlFor='outlined-adornment-deviceip'>
													Medidor IP
												</InputLabel>
												<OutlinedInput
													id='outlined-adornment-routerip'
													type={"text"}
													placeholder='Medidor IP'
													label='Medidor IP'
													value={device?.ipAddress}
													onChange={(event) => {
														setProp("ipAddress", event.target.value);
													}}
												/>
											</FormControl>
										</Box>
									)}
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Puerto
									</Typography>
									{!editModeState && (
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
											{device?.port}
										</Typography>
									)}
									{editModeState && (
										<Box sx={{ mt: 1, width: "100%" }}>
											<FormControl sx={{ width: "100%" }} variant='outlined'>
												<InputLabel htmlFor='outlined-adornment-deviceip'>
													Puerto
												</InputLabel>
												<OutlinedInput
													id='outlined-adornment-routerip'
													type='text'
													placeholder='Puerto'
													label='Puerto'
													value={device?.port}
													onChange={(event) => {
														setProp("port", Number(event.target.value));
													}}
												/>
											</FormControl>
										</Box>
									)}
								</Grid>
								<Grid item xs={12} md={6}>
									<Typography
										variant='caption'
										sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
										Notas
									</Typography>
									{!editModeState && (
										<Typography
											variant='body2'
											sx={{ color: "rgba(15, 23, 42, 0.8)" }}>
											{device?.notes}
										</Typography>
									)}
									{editModeState && (
										<Box sx={{ mt: 1, width: "100%" }}>
											<FormControl sx={{ width: "100%" }} variant='outlined'>
												<InputLabel htmlFor='outlined-adornment-description'>
													Notas
												</InputLabel>
												<OutlinedInput
													id='outlined-adornment-description'
													type={"text"}
													placeholder='Notas'
													multiline
													label='Notas'
													value={device?.notes}
													onChange={(event) => {
														setProp("notes", event.target.value);
													}}
												/>
											</FormControl>
										</Box>
									)}
								</Grid>

								{permissionaryConfiguration?.deviceHistoryEnabled && (
									<Grid item xs={12}>
										<Button
											disabled={isEmpty(
												(device as FetchDeviceWithHistoryDto).history,
											)}
											onClick={() => {
												navigate("/device-history/" + device?.id);
											}}
											variant='contained'
											sx={{
												mt: 1,
												borderRadius: 999,
												px: 3,
												py: 1.1,
												fontWeight: 700,
												background:
													"linear-gradient(135deg, #0ea5e9 0%, #0284c7 55%, #0369a1 100%)",
												boxShadow: "0 12px 20px rgba(2, 132, 199, 0.25)",
												"&:hover": {
													background:
														"linear-gradient(135deg, #0284c7 0%, #0369a1 70%, #075985 100%)",
												},
											}}>
											Ver historial
										</Button>
									</Grid>
								)}

								{derivedExceptions.length > 0 && (
									<Grid item xs={12}>
										<ExceptionsAccordion
											exceptions={derivedExceptions}
											label={`Errores Perfil de Carga (${derivedExceptions.length})`}
										/>
									</Grid>
								)}
							</Grid>
						</CardContent>
					</Card>
				</Box>
			)}
			<DeleteDeviceModal
				reactOnDelete={() => navigate("/devices")}
				deviceToDelete={deviceToDelete}
			/>
		</Box>
	);
}
